import "./App.css";
import { FC, useEffect, useState } from "react";
import Layout from "./modules/Layout";
import Alert from "./components/Alert";
import AppRoutes, { AppRoute } from "./utils/AppRoutes";
import { Subscribe } from "@react-rxjs/core";
import { Route, Routes } from "react-router-dom";
import { SessionTimeInMilliseconds } from "./utils";
import GlobalLoader from "./components/GlobalLoader";
import { useAppDispatch, useAppSelector } from "./store/hooks";
import { AppDataInterface, CountryInterface } from "./interfaces";
import {
  getCountries,
  logoutSession,
  getBaseAppData,
  getBusinessUnits,
  getTaxIdentificationTypes,
  alertService,
  getAppVersion,
  getMobilePaymentCoordinates,
  getVtid,
} from "./services";
import {
  logout,
  setInmutableAppData,
  setInmutableCountries,
  setInmutableRateItems,
  setInmutableCurrencyList,
  setInmutablePaymentModes,
  setInmutableExchangeRate,
  setInmutableBusinessUnits,
  setInmutablePieceCategories,
  setInmutableBusinessServices,
  setInmutableTaxIdentificationTypes,
  setInmutableRangeDeclareList,
  setInmutableVolumeMin,
  setInmutableHighDeclaredValue,
  setInmutableDensityMax,
  setAppVersion,
  setInmutableHolidays,
  setInmutableMobilePaymentCoordinates,
  setInmutableVTID,
} from "./store/slices";
import { getHolidays } from "./services/calendarService";
import Modal from "./components/Modal";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { PrimaryButton } from "./components/Buttons";

interface AppProps {}
const App: FC<AppProps> = () => {
  const dispatch = useAppDispatch();
  const version = useAppSelector((state) => state.app.version);
  const userConnected = useAppSelector((state) => state.user.user);
  const businessUnit = useAppSelector((state) => state.user.businessUnit);
  const userSession = useAppSelector((state) => state.user.userSession);
  const expirationTime = useAppSelector(
    (state) => state.user.userSession?.validUntil
  );
  const userLoginStartTime = useAppSelector(
    (state) => state.user.userLoginStartTime
  );
  const [openReloadModal, setOpenReloadModal] = useState(false);

  const countries = useAppSelector((state) => state.inmutable.countries);
  const venCountry = countries.find(
    (c: CountryInterface) => c.countryCodeIso === "VEN"
  );

  const [loggingOut, setLoggingOut] = useState(false);

  const filterRoute = (route: AppRoute) => {
    return (
      route.auth === !!userConnected &&
      (!route.rolesRequired ||
        (!!userConnected &&
          route.rolesRequired?.includes(userConnected.roleName))) &&
      (!route.filter || route.filter(userConnected ?? undefined, businessUnit))
    );
  };

  useEffect(() => {
    if (!userConnected || !userLoginStartTime || !userSession) return;

    const intervalId = setInterval(() => {
      const currentTime = new Date().getTime();
      const elapsedTime = currentTime - userLoginStartTime;
      const newRemainingTime = SessionTimeInMilliseconds - elapsedTime;

      //submethod to execute async logout
      const executeLogout = async () => {
        if (!userConnected || !userSession) return;
        setLoggingOut(true);
        const response = await logoutSession(
          userConnected.userID,
          userSession.sessionID
        );
        setLoggingOut(false);
        if (!response.didError) dispatch(logout());
      };

      // If remaining time is less than 0, logout
      if (
        !loggingOut &&
        (newRemainingTime <= 0 || localStorage.getItem("Token") === "end")
      ) {
        executeLogout();
      }
    }, 1000); // update every second

    return () => clearInterval(intervalId);
  }, [
    loggingOut,
    userConnected,
    dispatch,
    userLoginStartTime,
    userSession,
    expirationTime,
  ]);

  // Fetch countries and set them in the store
  useEffect(() => {
    const findCountries = async () => {
      const countries = await getCountries();
      if (!!countries) {
        dispatch(setInmutableCountries(countries));
      }
    };

    findCountries();
  }, [dispatch]);

  // Fetch app data and set them in the store
  useEffect(() => {
    const getAppData = async () => {
      const response = await getBaseAppData(venCountry?.id || null);

      if (!response.didError && !!response.model) {
        const appDataDto = response.model;
        // Set App Data
        dispatch(
          setInmutableAppData({
            applicationName: appDataDto.applicationName,
            applicationID: appDataDto.applicationID,
            applicationDescription: appDataDto.applicationDescription,
            applicationCode: appDataDto.applicationCode,
          } as AppDataInterface)
        );
        dispatch(setInmutableVolumeMin(appDataDto.volumenMin?.[0]));
        dispatch(setInmutableDensityMax(appDataDto.densityMax?.[0]));
        dispatch(
          setInmutableHighDeclaredValue(appDataDto.highDeclaredValue?.[0])
        );

        // Set Exchange Rate
        if (!!appDataDto.exchangeRate) {
          dispatch(setInmutableExchangeRate(appDataDto.exchangeRate));
        }
        // Set Business Services
        if (!!appDataDto.businessServiceList) {
          dispatch(
            setInmutableBusinessServices(appDataDto.businessServiceList)
          );
        }
        // Set Rate Item List
        if (!!appDataDto.rateItemList) {
          dispatch(setInmutableRateItems(appDataDto.rateItemList));
        }
        // Set Piece Categories
        if (!!appDataDto.shippingCategoryList) {
          const categories = appDataDto.shippingCategoryList.map((c) => ({
            id: c.categoryID,
            name: c.categoryName,
            code: c.categoryCode,
            descriptionTags: c.descriptionTags,
          }));
          dispatch(setInmutablePieceCategories(categories));
        }
        // Set Currency List
        if (!!appDataDto.currencyList) {
          dispatch(setInmutableCurrencyList(appDataDto.currencyList));
        }
        // Set Payment Mode List
        if (!!appDataDto.paymentModeList) {
          dispatch(setInmutablePaymentModes(appDataDto.paymentModeList));
        }
        // Set Range Declare List
        if (!!appDataDto.rangeDeclareList) {
          dispatch(setInmutableRangeDeclareList(appDataDto.rangeDeclareList));
        }

        // TODO add the rest of inmutable data
        //Load exchange rate every day at determined hours?
      } else {
        alertService.error(
          "Error al cargar los datos de la aplicación",
          response.errorMessage,
          { autoClose: false }
        );
      }
    };

    getAppData();
  }, [dispatch, venCountry?.id]);

  // Fetch stores
  useEffect(() => {
    const getAllBusinessUnits = async () => {
      const businessUnits = await getBusinessUnits();

      if (!!businessUnits) {
        dispatch(setInmutableBusinessUnits(businessUnits));
      }
    };

    getAllBusinessUnits();
  }, [dispatch]);

  useEffect(() => {
    const findTaxIdentificationTypes = async () => {
      const taxIdTypes = await getTaxIdentificationTypes();
      if (!!taxIdTypes) {
        dispatch(setInmutableTaxIdentificationTypes(taxIdTypes));
      }
    };

    findTaxIdentificationTypes();
  }, [dispatch]);

  useEffect(() => {
    const afunction = async () => {
      const response = await getHolidays();

      if (response.model) {
        dispatch(setInmutableHolidays(response.model));
      }
    };

    afunction();
  }, [dispatch]);

  useEffect(() => {
    const afunction = async () => {
      const response = await getAppVersion();

      if (!response.didError && response.model) {
        const { major, minor, revision } = response.model;
        const newVersion = `${major}.${minor}.${revision}`;

        if (version !== newVersion) {
          dispatch(setAppVersion(newVersion));
          setOpenReloadModal(true);
        }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        afunction();
      }
    };

    afunction();
    const intervalId = setInterval(afunction, 300000);
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch, version]);

  useEffect(() => {
    const getAllMobilePaymentCoordinates = async () => {
      const mobileCoordinates = await getMobilePaymentCoordinates();

      if (!!mobileCoordinates)
        dispatch(setInmutableMobilePaymentCoordinates(mobileCoordinates));
    };

    getAllMobilePaymentCoordinates();
  }, [dispatch]);

  useEffect(() => {
    const getAllVTID = async () => {
      const vtid = await getVtid(businessUnit?.code ?? "");

      if (!!vtid) dispatch(setInmutableVTID(vtid));
    };

    getAllVTID();
  }, [dispatch, businessUnit?.code]);

  return (
    <Subscribe>
      {!!userConnected && <Layout />}

      <Routes>
        {AppRoutes.filter(filterRoute).map((route, index) => {
          const { element, ...rest } = route;
          return <Route key={index} {...rest} element={element} />;
        })}
      </Routes>

      <Modal
        openModal={openReloadModal}
        setOpenModal={() => {
          setOpenReloadModal(false);
          window.location.reload();
        }}
      >
        <div className="flex flex-col gap-4 max-w-md mx-auto items-center">
          <div className="flex flex-col items-center">
            <ExclamationTriangleIcon className="text-orange-500 h-28 w-28 mx-auto opacity-90" />
            <h2 className="text-xl font-semibold leading-7 text-gray-700 text-center mt-2">
              Nueva versión de Innovus
            </h2>
          </div>

          <div className="flex flex-col gap-1">
            <p className="text-gray-600 text-center whitespace-pre-line">
              Hay una nueva actualización de Innovus disponible. Para continuar,
              es necesario recargar la página
            </p>
          </div>

          <div className="flex w-full flex-row gap-3 items-center justify-center mt-4">
            <PrimaryButton
              onClick={() => {
                setOpenReloadModal(false);
                window.location.reload();
              }}
              className="px-4 py-2"
            >
              Recargar
            </PrimaryButton>
          </div>
        </div>
      </Modal>

      <Alert />
      <GlobalLoader />
    </Subscribe>
  );
};
export default App;
